/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

package net.sergeych.crypto2

import kotlin.time.Instant
import kotlinx.serialization.Serializable
import net.sergeych.bipack.BipackEncoder
import net.sergeych.bipack.decodeFromBipack
import net.sergeych.crypto2.Seal.Companion.create
import net.sergeych.utools.now
import kotlin.time.Duration.Companion.seconds

/**
 * Extended public-key signature.
 *
 * See [Seal.create] for details and usage.
 *
 * This constructor normally should not be used directly, please use [Seal.create] instead
 *
 * @param publicKey the public key that could be used to verify the message. It could safely be published.
 * @param signature the signature generated by [create]. Do not generate it yourself.
 * @param nonce if [create] was called with `isDeterministic = true` there will be a random nonce.
 *          that makes it impossible to judge whether known seals are related to the same unknown message.
 * @param createdAt creation time as specified when creating.
 * @param expiresAt if set by creator, determines the time instant from when the seal is invalid.
 */
@Serializable
class Seal(
    val publicKey: VerifyingPublicKey,
    val signature: UByteArray,
    val nonce: UByteArray?,
    val createdAt: Instant,
    val expiresAt: Instant?,
) {

    /**
     * @suppress
     * This is the structure that is actually signed/verified with a key.
     */
    @Suppress("unused")
    @Serializable
    class SealedData(
        val message: UByteArray,
        val nonce: UByteArray?,
        val createdAt: Instant?,
        val validUntil: Instant?,
    )

    /**
     * Return true if the seal is correct, see [verify]
     */
    fun isValid(message: UByteArray): Boolean = kotlin.runCatching { verify(message) }.isSuccess

    /**
     * Return Result containing success or error reason if the seal is not correct
     */
    fun check(message: UByteArray): Result<Unit> = kotlin.runCatching { verify(message) }

    /**
     * Check that message is correct for this seal and throws exception if it is not.
     * Note that tampering [createdAt] and [expiresAt] invalidate the seal too.
     *
     * It checks first the signature. _If it is ok, it also checks_ [expiresAt].
     *
     * See [check] and [isValid] for non-throwing checks.
     *
     * @throws IllegalSignatureException if the signature is not valid for this [message]
     * @throws ExpiredSignatureException if the signature is valid, but [expiresAt] is not null and the seal
     *              is expired.
     */
    fun verify(message: UByteArray) {
        val n = now()
        if (createdAt - 45.seconds > n) throw IllegalSignatureException("signature's timestamp in the future: $createdAt / $n")
        expiresAt?.let {
            if (n >= it) throw ExpiredSignatureException("signature expired at $it")
        }
        val data = BipackEncoder.encode(SealedData(message, nonce, createdAt, expiresAt))
        if (!publicKey.verify(signature, data.toUByteArray()))
            throw IllegalSignatureException()
    }

    /**
     * Check that the seal is not expired.
     * __It is important__ that you can't determine whether the seal
     * is genuine and [expiresAt] not tampered if you do not have a message. If you do, be sure to call
     * [verify] or check [isValid] before calling this method.
     */
    fun isExpired(): Boolean = expiresAt?.let { it > now() } != false

    /**
     * In the rare cases you want to use it alone, the packed binary representation, use [unpack] to restore.
     * Most often, all you need is to put your Seal inside some kotlinx-serializable class.
     */
    val packed: UByteArray by lazy { BipackEncoder.encode(this).toUByteArray() }

    companion object {
        /**
         * Seal [message] with a [key].
         *
         * Seals are kotlinx-serializable and can be used
         * to check the authenticity of the arbitrary [message] using a public key, [VerifyingPublicKey]
         * instance, using public-key signing algorithms.
         *
         * Unlike a regular binary signature, Seal contains the signer's [EncryptingPublicKey], and also
         * [createdAt] and [expiresAt] fields which are also signed and are guaranteed to be non-tampered
         * if the [isValid] returns true (or [verify] does not throw). See [isExpired].
         *
         * It is important to understand that the seal itself could not be checked _having no message
         * it was created for_.
         * This is made intentionally: if you have no message, what means you are not the intended recipient, you can't
         * analyze the seal.
         *
         * To check that the message is genuine using a seal use:
         *
         * - [verify] which throws [IllegalSignatureException] or [ExpiredSignatureException] if it is not
         * - [check] that returns success or the exception without throwing it
         * - [isValid] that returns true or false, so you can't judge what was the reason (invalid signature or
         *              expiration, do not check for expiration _after_ false is returned as you can't trust
         *              into your seal yet).
         *
         * When you need to have a message and one or more seals all together, use [SealedBox].
         *
         * Please note for in the very rare key you want to trust the Seal having no message you need to create
         * another Seal to seal the seal. It sounds crazy as it is, and you should avoid such designs.
         *
         *
         * @param key secret key to sign with
         * @param message message to seal
         * @param createdAt seal creation time, usually current time
         * @param expiresAt optional seal expiration time
         * @param nonDeterministic if true, it is not possible to check whether two seals correspond to the same
         *          unknown message (if the message is known, it is trivial by verifying the seal). It is a
         *          rare case so default os false.
         */
        fun create(
            key: SigningSecretKey,
            message: UByteArray,
            createdAt: Instant = now(),
            expiresAt: Instant? = null,
            nonDeterministic: Boolean = false
        ): Seal {
            val nonce = if( nonDeterministic ) randomUBytes(32) else null
            val data = BipackEncoder.encode(SealedData(message, nonce, createdAt, expiresAt)).toUByteArray()
            return Seal(key.verifyingKey, key.sign(data), nonce, createdAt, expiresAt)
        }

        /**
         * Int the rare case you need a packed seal alone, unpack it. Normally just add seal to some [Serializable]
         * class, it is serializable.
         */
        fun unpack(packed: UByteArray): Seal = packed.asByteArray().decodeFromBipack()
    }
}